import { Chips } from "../Chips";
import { ProductGallery } from "../ProductGallery";
import { ProductOwner } from "../ProductOwner";
import { ReadMore } from "../ReadMore";
import { StrapiProduct } from "~/shared-types";
import { getOfferDetails } from "~/utils/getOfferDetails";
import { Badge } from "../Backroom/Badge";
import { EditMenu } from "./EditMenu";
import { PurchaseInfo } from "./PurchaseInfo";
import { getDeliveryDetails } from "~/utils/getDeliveryDetails";
import { stringToCurrency } from "~/utils/stringConversion";
import Icon from "~/assets/icons/Icon";
import { MarkdownReader } from "../MarkdownReader";
import { FavoriteButton } from "../FavoriteButton";

type Props = {
  product: StrapiProduct;
  isBackroom?: boolean;
  productAttributes?: { id: number; name: string; }[];
};

export const ProductInfo = ({ product, isBackroom, productAttributes = [] }: Props) => {
  const {
    name: productName,
    store: { data: store },
    danishDesign,
    danishProduction,
    danishMaterials,
    description,
    canOnlyBePickedUp,
    isUnique,
    subCategories,
    productTags,
  } = product.attributes;
  const filterValues = product.attributes.filterValues ?? {};

  const offerDetails = getOfferDetails(product);
  const { offersFreeDelivery, freeDeliveryPurchasePrice } =
    getDeliveryDetails(store);

  const declarations = [];
  if (danishDesign) declarations.push("Dansk design");
  if (danishProduction) declarations.push("Dansk produktion");
  if (danishMaterials) declarations.push("Danske materialer");

  return (
    <>
      {isBackroom ? <EditMenu product={product} /> : null}
      <div className="md:grid md:grid-cols-2 items-start xl:px-16 2xl:px-40 mb-20">
        <div>
          <ProductGallery product={product} />
          <div className="flex w-full items-center justify-end -mt-8 md:hidden gap-2">
            {productTags?.data?.map((tag) => (
              <Badge key={tag.id} className="mr-2" color="default">
                {tag.attributes.title}
              </Badge>
            ))}
            {isUnique && (
              <Badge className="mr-2" color="unique">
                Unika
              </Badge>
            )}
            <FavoriteButton
              className="-mr-2"
              favorite={{ id: product.id, type: "product" }}
            />
          </div>
        </div>

        <div className="md:pl-7 xl:pl-20">
          <ProductOwner store={store} />

          <h1 className="text-2xl mb-5">{productName}</h1>
          <Chips className="mb-14" texts={declarations} />
          {Object.keys(filterValues).length > 0 && (
            <div className="flex flex-col gap-2 mb-14">
              {Object.entries(filterValues).map(([attrId, vals]) => {
                const attrName = productAttributes.find(a => a.id === parseInt(attrId))?.name;
                return (
                  <div key={attrId} className="flex gap-2 items-center text-sm flex-wrap">
                    {attrName && <span className="text-secondary-600 font-medium">{attrName}:</span>}
                    <div className="flex gap-1 flex-wrap">
                      {vals.map((v: string) => (
                        <span key={v} className="bg-secondary-100 rounded-md px-2 py-0.5">{v}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <ReadMore className="mb-14">
            <MarkdownReader className="text-secondary-800">
              {description}
            </MarkdownReader>
          </ReadMore>
          {offerDetails.hasCurrentOffer ? (
            <div className="mb-4">
              <Badge color="yellow">{offerDetails.offerTitle}</Badge>
              <span className="text-secondary-500 text-xs italic">
                Udløber om {offerDetails.daysTillOfferEnds} dag(e)
              </span>
            </div>
          ) : null}
          <PurchaseInfo product={product} isBackroom={isBackroom} />

          {!canOnlyBePickedUp && offersFreeDelivery && (
            <div className="text-secondary-500 text-xs items-center rounded-md bg-secondary-100 p-2 mt-2 inline-block">
              <Icon
                name="shippingSide"
                className="text-warning w-6 h-6 inline mr-2"
              />
              <span>
                Gratis levering ved køb over{" "}
                <span className="font-medium">
                  {stringToCurrency({ amount: freeDeliveryPurchasePrice })}
                </span>
                .
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
